/**
 * CollapsedSelectSlot
 *
 * Icon-only sidebar shown when ResizableSidebar is collapsed. Each section
 * is rendered as a single icon button, which either floats the real
 * expanded sidebar content as an overlay (`onIconClick`) or opens a small
 * fixed-position popover with its own `content` to the right.
 */
import { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import { createPortal } from 'react-dom';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { useSidebarTriggerMode } from '@/lib/utils/sidebarTriggerMode';

/* ── Popover ────────────────────────────────────────────── */

/** Fixed-position popover anchored to the right of the collapsed sidebar. */
function SidebarPopover({
  anchorRef,
  children,
  onClose,
  onMouseEnter,
  onMouseLeave,
  overflowVisible = false,
}: {
  anchorRef: React.RefObject<HTMLElement>;
  children: React.ReactNode;
  onClose: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  /** Set true when the popover contains position:absolute dropdowns that must not be clipped. */
  overflowVisible?: boolean;
}) {
  const popRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setStyle({
      position: 'fixed',
      top: rect.top,
      left: rect.right + 6,
      zIndex: 1200,
    });
  }, [anchorRef]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Element | null;
      // MultiSelect (country/language) dropdowns and Radix Popover content (e.g.
      // SubscriberRangeInput's tier picker) are portaled to <body>, so a click inside one is
      // "outside" this popover's DOM — exempt both, else picking an option closes the flyout
      // before the selection's onChange fires.
      if (target?.closest?.('[data-ms-portal], [data-radix-popper-content-wrapper]')) return;
      if (
        popRef.current && !popRef.current.contains(e.target as Node) &&
        anchorRef.current && !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, [onClose, anchorRef]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return createPortal(
    <div
      ref={popRef}
      style={style}
      className={`collapsed-sidebar-popover${overflowVisible ? ' collapsed-sidebar-popover--overflow-visible' : ''}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </div>,
    document.body,
  );
}

/* ── Icon button ─────────────────────────────────────────── */

function IconBtn({
  icon,
  tooltip,
  active,
  current,
  onClick,
  onMouseEnter,
  onMouseLeave,
  btnRef,
  badge,
  showTooltip = false,
}: {
  icon: string;
  tooltip: string;
  active?: boolean;
  /** Highlight (hover-like) when the current route belongs to this icon's section. */
  current?: boolean;
  onClick: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  btnRef?: React.Ref<HTMLButtonElement>;
  badge?: number;
  /** Show tooltip on hover (click mode only). Uses controlled Radix open to avoid remounting. */
  showTooltip?: boolean;
}) {
  return (
    <TooltipPrimitive.Root open={showTooltip ? undefined : false} delayDuration={500}>
      <TooltipPrimitive.Trigger asChild>
        <button
          ref={btnRef}
          type="button"
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          className={`collapsed-sidebar-icon-btn${current ? ' collapsed-sidebar-icon-btn--current' : ''}${active ? ' collapsed-sidebar-icon-btn--active' : ''}`}
          aria-label={tooltip}
        >
          <i className={`fa ${icon}`} aria-hidden />
          {!!badge && badge > 0 && (
            <span className="action-btn-badge" style={{ top: -4, right: -4, fontSize: 8, minWidth: 12, padding: '0 3px' }}>
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </button>
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side="right"
          sideOffset={8}
          className="z-[1300] rounded-ggc-sm bg-[#1e293b] px-2 py-1 ggc-text-caption-m text-white shadow-ggc-md"
        >
          {tooltip}
          <TooltipPrimitive.Arrow className="fill-[#1e293b]" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

/* ── Main Component ──────────────────────────────────────── */

/* ── CollapsedSelectSlot ─────────────────────────────────
 * Generic multi-section icon strip.
 * Each section renders as one icon button; clicking it opens
 * a fixed popover to the right with arbitrary content.
 * Used by the influencers and rosters pages.
 * ─────────────────────────────────────────────────────── */

export interface CollapsedSlotSection {
  /** Unique key for this section */
  key: string;
  /** FontAwesome icon class, e.g. "fa-gift" */
  icon: string;
  tooltip: string;
  /** Popover content. Ignored when `onIconClick` is set. */
  content?: React.ReactNode;
  /** Width of the popover (default 260) */
  popoverWidth?: number;
  /** Allow position:absolute children to overflow (e.g. campaign dropdown) */
  overflowVisible?: boolean;
  /** When set, clicking this icon calls this instead of opening a popover — e.g. to
   *  temporarily expand the full sidebar for this section instead of duplicating its form
   *  in a popover. `content`/`popoverWidth`/`overflowVisible` are unused in this mode. */
  onIconClick?: () => void;
}

export function CollapsedSelectSlot({ sections }: { sections: CollapsedSlotSection[] }) {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const btnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerMode = useSidebarTriggerMode();
  const isHover = triggerMode === 'hover';

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
  }, []);
  const scheduleClose = useCallback(() => {
    closeTimerRef.current = setTimeout(() => setOpenKey(null), 200);
  }, []);
  const close = useCallback(() => { cancelClose(); setOpenKey(null); }, [cancelClose]);
  const openSection = useCallback((key: string) => { cancelClose(); setOpenKey(key); }, [cancelClose]);
  const toggle = useCallback((key: string) => {
    setOpenKey((prev) => (prev === key ? null : key));
  }, []);

  return (
    <div className="collapsed-sidebar-icons">
      {sections.map((section, idx) => (
        <Fragment key={section.key}>
          {idx > 0 && <div className="collapsed-sidebar-divider" />}
          <IconBtn
            btnRef={(el) => { btnRefs.current[section.key] = el; }}
            icon={section.icon}
            tooltip={section.tooltip}
            active={openKey === section.key}
            // onIconClick sections never open a popover on hover (there's nothing else to
            // reveal their label), so their tooltip should always be allowed regardless of
            // trigger mode — only popover sections suppress it while hover-mode already shows
            // the popover itself.
            showTooltip={section.onIconClick ? true : (!isHover && openKey !== section.key)}
            onClick={() => {
              if (section.onIconClick) { section.onIconClick(); return; }
              isHover ? openSection(section.key) : toggle(section.key);
            }}
            onMouseEnter={isHover && !section.onIconClick ? () => openSection(section.key) : undefined}
            onMouseLeave={isHover && !section.onIconClick ? scheduleClose : undefined}
          />
          {!section.onIconClick && openKey === section.key && btnRefs.current[section.key] && (
            <SidebarPopover
              anchorRef={{ current: btnRefs.current[section.key] } as React.RefObject<HTMLElement>}
              onClose={close}
              onMouseEnter={isHover ? cancelClose : undefined}
              onMouseLeave={isHover ? scheduleClose : undefined}
              overflowVisible={section.overflowVisible}
            >
              <div style={{ width: section.popoverWidth ?? 260 }}>
                {section.content}
              </div>
            </SidebarPopover>
          )}
        </Fragment>
      ))}
    </div>
  );
}
